import { Card, Button, Badge } from '@repo/ui-core'
import { useState } from 'react'
import { Plus, Search, FileText, X } from 'lucide-react'
import type { SalesOrder } from '@repo/entity-accounting'

export function SalesOrdersPage() {
  const [orders, setOrders] = useState<SalesOrder[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Form State
  const [customerName, setCustomerName] = useState('')
  const [lines, setLines] = useState([{ description: '', quantity: 1, unitPrice: 0 }])

  const handleAddLine = () => {
    setLines([...lines, { description: '', quantity: 1, unitPrice: 0 }])
  }

  const handleSave = () => {
    const newSo: SalesOrder = {
      id: `so-${Date.now()}`,
      tenantId: 'default',
      soNumber: `SO-${Date.now()}`,
      customerId: 'cust-1',
      customerName,
      orderDate: Date.now(),
      subtotal: lines.reduce((acc, line) => acc + (line.quantity * line.unitPrice), 0),
      taxAmount: 0, // simple mock
      totalAmount: lines.reduce((acc, line) => acc + (line.quantity * line.unitPrice), 0),
      status: 'draft',
      lines: lines.map((l, i) => ({
        id: `l${i}`,
        itemId: `item${i}`,
        description: l.description,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        amount: l.quantity * l.unitPrice
      })),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null
    }
    setOrders([newSo, ...orders])
    setIsModalOpen(false)
    setCustomerName('')
    setLines([{ description: '', quantity: 1, unitPrice: 0 }])
  }

  const statusColors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-800',
    confirmed: 'bg-blue-100 text-blue-800',
    delivered: 'bg-yellow-100 text-yellow-800',
    invoiced: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Sales Orders
          </h1>
          <p className="text-gray-500 mt-1">Manage customer orders and generate invoices (₱ PHP)</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4" />
          Create Sales Order
        </Button>
      </div>

      <Card className="bg-white p-4">
        <div className="flex items-center gap-2 mb-4 border rounded-md px-3 py-2">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by SO number or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full outline-none bg-transparent"
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-gray-500 animate-pulse">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center">
            <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No sales orders found</h3>
            <p className="text-gray-500 mt-1">Create your first sales order to start tracking revenue.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b bg-gray-50 text-sm font-medium text-gray-600">
                  <th className="py-3 px-4">SO Number</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Customer</th>
                  <th className="py-3 px-4 text-right">Total Amount (₱)</th>
                  <th className="py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map((so) => (
                  <tr key={so.id} className="hover:bg-gray-50/50">
                    <td className="py-3 px-4 font-medium text-gray-900">{so.soNumber}</td>
                    <td className="py-3 px-4 text-gray-500">
                      {new Date(so.orderDate).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-gray-900">{so.customerName}</td>
                    <td className="py-3 px-4 text-right font-medium">
                      ₱{so.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={`uppercase text-xs tracking-wider ${statusColors[so.status] || statusColors.draft}`}>
                        {so.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Create Sales Order</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Line Items</label>
                  <Button variant="outline" size="sm" onClick={handleAddLine}>Add Line</Button>
                </div>
                
                <div className="space-y-2">
                  {lines.map((line, idx) => (
                    <div key={idx} className="flex gap-2 items-center bg-gray-50 p-2 rounded-md border">
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => {
                          const newLines = [...lines]
                          const l = newLines[idx]
                          if (l) { l.description = e.target.value; setLines(newLines) }
                        }}
                        className="flex-1 border rounded-md px-2 py-1 text-sm outline-none"
                        placeholder="Description"
                      />
                      <input
                        type="number"
                        value={line.quantity}
                        onChange={e => {
                          const newLines = [...lines]
                          const l = newLines[idx]
                          if (l) { l.quantity = Number(e.target.value); setLines(newLines) }
                        }}
                        className="w-20 border rounded-md px-2 py-1 text-sm outline-none"
                        placeholder="Qty"
                      />
                      <input
                        type="number"
                        value={line.unitPrice}
                        onChange={e => {
                          const newLines = [...lines]
                          const l = newLines[idx]
                          if (l) { l.unitPrice = Number(e.target.value); setLines(newLines) }
                        }}
                        className="w-24 border rounded-md px-2 py-1 text-sm outline-none"
                        placeholder="Price (₱)"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={handleSave}>Save Order</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesOrdersPage
